import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Users, ChevronRight, Bell, BellOff, Building2, Award, HelpCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { SCOUT_LEVEL_THRESHOLDS, SCOUT_POINT_VALUES } from '@tendedloop/shared';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { formatNumber } from '@/utils/formatDate';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { rankName } from '@/utils/rankName';

export default function ProfilePage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { scout, isAuthenticated } = useAuthStore();
  const {
    isSupported: pushSupported,
    isSubscribed: pushEnabled,
    subscribe: enablePush,
    unsubscribe: disablePush,
  } = usePushNotifications();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (!scout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const currentLevel = SCOUT_LEVEL_THRESHOLDS.find((l) => l.level === scout.level) ?? SCOUT_LEVEL_THRESHOLDS[0];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-warm-faint">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <h1 className="text-section font-semibold text-foreground">{t('profile.title')}</h1>
      </div>

      {/* Profile */}
      <div className="px-4 space-y-4">
        {/* Avatar & Name */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary/10 to-forest/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            <img
              src={`/images/ranks/rank-${scout.level}.png`}
              alt={rankName(currentLevel.level, currentLevel.name)}
              className="w-12 h-12 object-contain"
            />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-foreground truncate">{scout.name || scout.email.split('@')[0]}</p>
            <p className="text-small text-muted-foreground truncate">{scout.email}</p>
            <p className="text-caption text-primary font-medium">{rankName(currentLevel.level, currentLevel.name)}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
          <h3 className="text-small font-semibold text-foreground mb-3">{t('profile.stats')}</h3>
          <div className="grid grid-cols-2 gap-3">
            <Stat label={t('profile.totalXp')} value={formatNumber(scout.totalXp)} />
            <Stat label={t('profile.level')} value={String(scout.level)} />
            <Stat label={t('profile.totalScans')} value={formatNumber(scout.totalScans)} />
            <Stat label={t('profile.currentStreak')} value={String(scout.currentStreak)} />
            <Stat label={t('profile.longestStreak')} value={String(scout.longestStreak)} />
            <Stat label={t('profile.issuesReported')} value={formatNumber(scout.totalIssues)} />
            <Stat label={t('profile.referrals')} value={formatNumber(scout.totalReferrals)} />
            <Stat label={t('profile.spendableXp')} value={formatNumber(scout.currentXp)} />
          </div>
        </div>

        {/* Notifications */}
        {pushSupported && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
            <h3 className="text-small font-semibold text-foreground mb-3">{t('push.settings')}</h3>
            <button
              onClick={() => (pushEnabled ? disablePush() : enablePush())}
              className="w-full flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                {pushEnabled ? (
                  <Bell className="w-5 h-5 text-primary" />
                ) : (
                  <BellOff className="w-5 h-5 text-muted-foreground" />
                )}
                <span className="text-small text-foreground">
                  {pushEnabled ? t('push.enabled') : t('push.disabled')}
                </span>
              </div>
              <div
                className={`w-10 h-6 rounded-full transition-colors ${
                  pushEnabled ? 'bg-primary' : 'bg-warm-border'
                } relative`}
              >
                <div
                  className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${
                    pushEnabled ? 'start-[18px]' : 'start-0.5'
                  }`}
                />
              </div>
            </button>
          </div>
        )}

        {/* Organizations */}
        <button
          onClick={() => navigate('/organizations')}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-steel/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 text-steel" />
          </div>
          <div className="flex-1 text-start min-w-0">
            <p className="text-small font-semibold text-foreground">{t('organizations.title')}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>

        {/* Badges (moved from bottom nav) */}
        <button
          onClick={() => navigate('/badges')}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-forest/10 flex items-center justify-center flex-shrink-0">
            <Award className="w-5 h-5 text-forest" />
          </div>
          <div className="flex-1 text-start min-w-0">
            <p className="text-small font-semibold text-foreground">
              {t('profile.badges', { defaultValue: 'Badges' })}
            </p>
            <p className="text-caption text-muted-foreground">
              {t('profile.badgesHint', { defaultValue: 'View your earned badges and achievements' })}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>

        {/* Awards */}
        <button
          onClick={() => navigate('/awards')}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-gold/10 flex items-center justify-center flex-shrink-0">
            <Award className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1 text-start min-w-0">
            <p className="text-small font-semibold text-foreground">{t('awards.title')}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>

        {/* How it works */}
        <button
          onClick={() => {
            localStorage.removeItem('scout_onboarded');
            navigate('/');
          }}
          className="w-full bg-white rounded-xl p-4 shadow-sm border border-warm-border flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <HelpCircle className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 text-start min-w-0">
            <p className="text-small font-semibold text-foreground">{t('profile.howItWorks')}</p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>

        {/* Language */}
        <LanguageSwitcher />

        {/* Invite Friends */}
        <button
          onClick={() => navigate('/referrals')}
          className="w-full bg-gradient-to-r from-primary/5 to-forest-wash rounded-xl p-4 shadow-sm border border-forest/20 flex items-center gap-3"
        >
          <div className="w-10 h-10 rounded-lg bg-forest-wash flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-forest" />
          </div>
          <div className="flex-1 text-start min-w-0">
            <p className="text-small font-semibold text-foreground">{t('profile.inviteFriends')}</p>
            <p className="text-caption text-muted-foreground">
              {t('profile.inviteFriendsHint', { xp: SCOUT_POINT_VALUES.REFERRAL_BONUS })}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-section font-bold text-foreground">{value}</p>
      <p className="text-caption text-muted-foreground">{label}</p>
    </div>
  );
}
